import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ListingCard from '../components/ListingCard';
import ShareButtons from '../components/ShareButtons';
import { ShopDocumentsPublic } from '../components/ShopDocuments';
import { StarRating } from '../components/ReviewSection';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { buildShopShareUrl } from '../lib/share';
import { setPageMeta, resetPageMeta } from '../lib/seo';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  MapPin, Mail, Phone, Globe, Building2, FileText, User, Store, Edit,
  MessageSquare, ArrowLeft, ShieldCheck, Star, Clock, ChevronDown, Send, Heart, ShoppingCart, ExternalLink,
  Crown, Medal, Award, Trophy
} from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';
import StandaloneHeader from '../components/StandaloneHeader';
import CompanyFooter from '../components/CompanyFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_NAMES = {
  riga: 'Rīga', jurmala: 'Jūrmala', liepaja: 'Liepāja', ventspils: 'Ventspils',
  daugavpils: 'Daugavpils', rezekne: 'Rēzekne', jelgava: 'Jelgava', jekabpils: 'Jēkabpils',
  valmiera: 'Valmiera', ogre: 'Ogre', cesis: 'Cēsis', sigulda: 'Sigulda',
  kuldiga: 'Kuldīga', saldus: 'Saldus', bauska: 'Bauska', madona: 'Madona',
  talsi: 'Talsi', tukums: 'Tukums', dobele: 'Dobele', balvi: 'Balvi',
};

const ENTITY_LABELS = {
  lv: { sia: 'SIA', ik: 'IK', person: 'Civilpersona' },
  en: { sia: 'Ltd (SIA)', ik: 'Sole Proprietor', person: 'Individual' }
};

export default function ShopPage() {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [shopData, setShopData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ avg_rating: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [messageText, setMessageText] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [customization, setCustomization] = useState(null);
  const [collections, setCollections] = useState([]); // shop-defined groups
  const [activeCollection, setActiveCollection] = useState(null); // collection id filter, null = show all
  const [topBadges, setTopBadges] = useState([]);
  const [trustScore, setTrustScore] = useState(null);
  const [themedHtml, setThemedHtml] = useState(null);
  const [forceStandard, setForceStandard] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [shopRes, reviewsRes, customRes, collectionsRes, badgesRes, trustRes] = await Promise.all([
          axios.get(`${API}/shop/${userId}`),
          axios.get(`${API}/reviews/seller/${userId}`),
          axios.get(`${API}/shop-customization/${userId}`).catch(() => ({ data: null })),
          axios.get(`${API}/shops/${userId}/collections`).catch(() => ({ data: [] })),
          axios.get(`${API}/shop/${userId}/badges`).catch(() => ({ data: { badges: [] } })),
          axios.get(`${API}/shops/${userId}/trust-score`).catch(() => ({ data: null })),
        ]);
        setShopData(shopRes.data);
        setReviews(reviewsRes.data.reviews || []);
        setReviewStats({ avg_rating: reviewsRes.data.avg_rating, total: reviewsRes.data.total });
        if (customRes.data && Object.keys(customRes.data).length) {
          setCustomization(customRes.data);
          if (customRes.data.theme_default) {
            try {
              const tr = await axios.get(`${API}/shop-customization/${userId}/themed`);
              if (tr.data && tr.data.html) setThemedHtml(tr.data.html);
            } catch (e) { /* ignore */ }
          }
        }
        setCollections((collectionsRes.data || []).filter(c => c.is_public !== false));
        setTopBadges(badgesRes.data?.badges || []);
        setTrustScore(trustRes.data);

        // Per-page SEO + Organization JSON-LD for Google rich results
        const origin = window.location.origin;
        const user = shopRes.data?.user || {};
        const profile = shopRes.data?.shop_profile || {};
        const shopName = profile.shop_name || user.name || 'KoLV Shop';
        const cover = profile.shop_cover_url || profile.shop_logo_url;
        const absCover = cover && cover.startsWith('/') ? `${origin}${cover}` : cover;
        const desc = (profile.shop_description || `Iepazīsti ${shopName} veikalu KoLV — Latvijas reģionālā tirgus vietā.`).slice(0, 220);
        setPageMeta({
          title: `${shopName} | KoLV`,
          description: desc,
          image: absCover,
          url: `${origin}/shop/${userId}`,
          type: 'website',
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": shopName,
            "description": desc,
            "image": absCover || undefined,
            "url": `${origin}/shop/${userId}`,
            ...(profile.legal_address ? { "address": profile.legal_address } : {}),
            ...(profile.shop_phone ? { "telephone": profile.shop_phone } : {}),
          },
        });
      } catch (e) { navigate('/'); }
      finally { setLoading(false); }
    };
    fetchAll();
    return () => { resetPageMeta(); };
  }, [userId, navigate]);

  // Load Google Font dynamically if the shop uses a non-default family
  useEffect(() => {
    if (!customization?.font_family) return;
    const map = {
      playfair: 'Playfair+Display:wght@400;600;700',
      merriweather: 'Merriweather:wght@400;700',
      montserrat: 'Montserrat:wght@400;600;700',
      figtree: 'Figtree:wght@400;600;700',
      inter: 'Inter:wght@400;600;700',
    };
    const q = map[customization.font_family];
    if (!q) return; // Outfit already loaded
    const id = `shop-font-${customization.font_family}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${q}&display=swap`;
    document.head.appendChild(link);
  }, [customization?.font_family]);

  const handleSendMessage = async () => {
    if (!messageText.trim() || !currentUser) return;
    try {
      await axios.post(`${API}/messages`, { recipient_id: userId, content: messageText });
      setMessageText('');
      setMessageSent(true);
      setTimeout(() => setMessageSent(false), 3000);
    } catch (e) { console.error(e); }
  };

  // Sync collection state ↔ ?collection= URL param. When `collections` first
  // loads, resolve the slug from the URL to its concrete id. When the user
  // toggles a collection card, push the new slug to the URL so the link is
  // sharable + browser back/forward works.
  useEffect(() => {
    if (!collections.length) return;
    const slug = searchParams.get('collection');
    if (!slug) {
      // URL has no slug — make sure no collection is selected
      if (activeCollection) setActiveCollection(null);
      return;
    }
    const match = collections.find(c => c.slug === slug);
    if (match && match.id !== activeCollection) {
      setActiveCollection(match.id);
    } else if (!match) {
      // Slug doesn't resolve — clear the bad param silently
      searchParams.delete('collection');
      setSearchParams(searchParams, { replace: true });
    }
  }, [collections, searchParams, activeCollection, setSearchParams]);

  const handleCollectionToggle = (coll) => {
    const isActive = activeCollection === coll.id;
    const next = new URLSearchParams(searchParams);
    if (isActive) {
      next.delete('collection');
      setActiveCollection(null);
    } else {
      next.set('collection', coll.slug || coll.id);
      setActiveCollection(coll.id);
    }
    setSearchParams(next, { replace: false });
  };

  const handleClearCollection = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('collection');
    setSearchParams(next, { replace: false });
    setActiveCollection(null);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]" />
    </div>
  );

  if (!shopData) return null;

  const shopPausedDM = shopData.is_paused && shopData.pause_until ? (() => { const d = new Date(shopData.pause_until); return isNaN(d) ? '' : `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}`; })() : '';
  const PausedBanner = shopData.is_paused ? (
    <div className="bg-amber-50 border-b border-amber-300" data-testid="shop-paused-banner">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <Clock className="w-5 h-5 text-[#D97736] flex-shrink-0" />
        <p className="text-sm font-medium text-[#8a5a1f]" data-testid="shop-paused-title">
          {shopPausedDM
            ? (language === 'lv' ? `Šis veikals ir pauzēts līdz ${shopPausedDM} — pasūtījumi pagaidām nav pieejami` : language === 'ru' ? `Магазин приостановлен до ${shopPausedDM} — заказы временно недоступны` : `This shop is paused until ${shopPausedDM} — orders are temporarily unavailable`)
            : (language === 'lv' ? 'Šis veikals īslaicīgi ir pauzēts — pasūtījumi pagaidām nav pieejami' : language === 'ru' ? 'Магазин временно приостановлен — заказы недоступны' : 'This shop is temporarily paused — orders are unavailable')}
        </p>
      </div>
    </div>
  ) : null;

  if (themedHtml && !forceStandard) {
    return (
      <div data-testid="shop-themed-view">
        <StandaloneHeader theme="light" />
        {PausedBanner}
        <div className="flex items-center justify-end gap-3 px-4 py-2 bg-[#121212] text-white text-xs">
          <span className="opacity-70">{language === 'lv' ? 'Pielāgota veikala tēma' : language === 'ru' ? 'Персональная тема магазина' : 'Custom shop theme'}</span>
          <button onClick={() => setForceStandard(true)} className="underline" data-testid="switch-standard-view">
            {language === 'lv' ? 'Standarta skats' : language === 'ru' ? 'Стандартный вид' : 'Standard view'}
          </button>
        </div>
        <iframe title="themed-shop" sandbox="allow-same-origin" srcDoc={themedHtml}
          className="w-full border-0" style={{ height: 'calc(100vh - 33px)' }} data-testid="shop-themed-frame" />
      </div>
    );
  }

  const { user: shopUser, shop_profile, listings } = shopData;
  const isOwner = currentUser && currentUser._id === shopUser._id;
  const shopName = shop_profile?.shop_name || shopUser.name;
  const shopDesc = shop_profile?.shop_description || shopUser.bio || '';

  const categoryTabs = ['all', 'shop', 'service', 'event', 'project', 'business'];
  const categoryLabels = {
    all: language === 'lv' ? 'Visi' : language === 'ru' ? 'Все' : 'All',
    shop: language === 'lv' ? 'Veikals' : language === 'ru' ? 'Магазин' : 'Shop',
    service: language === 'lv' ? 'Pakalpojumi' : language === 'ru' ? 'Услуги' : 'Services',
    event: language === 'lv' ? 'Pasākumi' : language === 'ru' ? 'Мероприятия' : 'Events',
    project: language === 'lv' ? 'Projekti' : language === 'ru' ? 'Проекты' : 'Projects',
    business: language === 'lv' ? 'Uzņēmumi' : language === 'ru' ? 'Компании' : 'Businesses',
  };
  // Combined filter: collection (if set) AND category tab.
  const filteredListings = listings.filter(l => {
    if (activeCollection && !(l.collection_ids || []).includes(activeCollection)) return false;
    if (activeTab !== 'all' && l.category !== activeTab) return false;
    return true;
  });

  // Resolve customization → inline style values (all fall back to site defaults)
  const fontMap = {
    outfit: "'Outfit', sans-serif",
    figtree: "'Figtree', sans-serif",
    inter: "'Inter', sans-serif",
    playfair: "'Playfair Display', serif",
    merriweather: "'Merriweather', serif",
    montserrat: "'Montserrat', sans-serif",
  };
  const c = customization || {};
  const shopStyle = customization ? {
    backgroundColor: c.bg_color || '#FAFAFA',
    color: c.text_color || '#121212',
    fontFamily: fontMap[c.font_family] || fontMap.outfit,
  } : {};
  const primary = c.primary_color || '#4A6B53';
  const secondary = c.secondary_color || '#D97736';
  const heroGradient = customization
    ? `linear-gradient(135deg, ${primary} 0%, ${primary}dd 50%, ${primary} 100%)`
    : undefined;

  // Premium template style tokens, scoped to #kolv-shop.
  const TEMPLATE_CSS = {
    minimalist: '#kolv-shop h1,#kolv-shop h2{letter-spacing:-.02em;font-weight:600}#kolv-shop .rounded-2xl,#kolv-shop .rounded-xl{border-radius:8px}#kolv-shop *{box-shadow:none!important}',
    bold: '#kolv-shop h1{font-weight:800;letter-spacing:-.03em;text-transform:uppercase}#kolv-shop h2{font-weight:700}',
    vintage: '#kolv-shop{font-family:"Merriweather",serif}#kolv-shop h1,#kolv-shop h2{font-family:"Playfair Display",serif}',
    craft: '#kolv-shop .rounded-xl,#kolv-shop .rounded-2xl{border-radius:20px}#kolv-shop section{border-radius:20px}',
    modern: '#kolv-shop .rounded-xl{border-radius:6px}#kolv-shop h1{font-weight:700}',
  };
  const combinedCss = customization
    ? `${c.template ? (TEMPLATE_CSS[c.template] || '') : ''}\n${c.custom_css || ''}`
    : '';

  return (
    <div
      id="kolv-shop"
      className={customization ? "min-h-screen" : "min-h-screen bg-[#FAFAFA]"}
      style={shopStyle}
      data-testid="shop-page"
    >
      {combinedCss.trim() && <style dangerouslySetInnerHTML={{ __html: combinedCss }} />}
      <StandaloneHeader theme="light" />
      {PausedBanner}
      {c.banner_top_html && (
        <div data-testid="shop-banner-top" dangerouslySetInnerHTML={{ __html: c.banner_top_html }} />
      )}
      {/* ===== HERO SECTION ===== */}
      <section
        className="relative h-[50vh] min-h-[360px] max-h-[480px] overflow-hidden"
        style={{
          background: heroGradient || 'linear-gradient(135deg, #1a2e1f 0%, #2d4a35 50%, #1a2e1f 100%)',
        }}
      >
        {shop_profile?.shop_cover_url && (shop_profile.shop_cover_url.startsWith('http') || shop_profile.shop_cover_url.startsWith('/')) && !shop_profile.shop_cover_url.includes('example.com') && (
          <img src={resolveAssetUrl(shop_profile.shop_cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" onError={(e) => { e.target.style.display = 'none'; }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-6 left-6 z-10 flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white/90 hover:bg-white/20 transition-colors text-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}
        </button>

        {isOwner && (
          <Link to="/shop-profile" className="absolute top-6 right-6 z-10">
            <Button variant="outline" size="sm" className="bg-white/10 backdrop-blur-md border-white/20 text-white hover:bg-white/20">
              <Edit className="w-4 h-4 mr-1" /> {t('editShop')}
            </Button>
          </Link>
        )}

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
          <div className="max-w-5xl mx-auto flex items-end gap-6">
            {/* Logo/Avatar */}
            <div
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white shadow-2xl flex items-center justify-center text-4xl font-bold flex-shrink-0 border-4 border-white overflow-hidden"
              style={{ color: secondary }}
            >
              {shop_profile?.shop_logo_url && (shop_profile.shop_logo_url.startsWith('http') || shop_profile.shop_logo_url.startsWith('/')) && !shop_profile.shop_logo_url.includes('example.com') ? (
                <img src={resolveAssetUrl(shop_profile.shop_logo_url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.textContent = shopName.charAt(0).toUpperCase(); }} />
              ) : (
                shopName.charAt(0).toUpperCase()
              )}
            </div>

            <div className="flex-1 text-white pb-1">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h1 className="font-outfit text-3xl sm:text-4xl font-bold drop-shadow-lg">
                  {shopName}
                </h1>
                <div className="hidden sm:block">
                  <ShareButtons
                    title={shopName}
                    shareUrl={buildShopShareUrl(shopUser._id || shopUser.id)}
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {shop_profile?.is_verified && (
                  <Badge className="bg-green-500/90 text-white border-0 backdrop-blur-sm" data-testid="verified-badge">
                    <ShieldCheck className="w-3 h-3 mr-1" /> {language === 'lv' ? 'Verificēts' : language === 'ru' ? 'Проверено' : 'Verified'}
                  </Badge>
                )}
                {shop_profile?.entity_type && (
                  <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                    {ENTITY_LABELS[language]?.[shop_profile.entity_type]}
                  </Badge>
                )}
                {shopUser.region && (
                  <span className="flex items-center gap-1 text-sm text-white/80">
                    <MapPin className="w-3.5 h-3.5" />
                    {REGION_NAMES[shopUser.region] || shopUser.region}
                  </span>
                )}
                {reviewStats.total > 0 && (
                  <span className="flex items-center gap-1 text-sm text-white/80">
                    <Star className="w-3.5 h-3.5" style={{ fill: secondary, color: secondary }} />
                    {reviewStats.avg_rating} ({reviewStats.total})
                  </span>
                )}
                <span className="text-sm text-white/60">
                  {listings.length} {language === 'lv' ? 'sludinājumi' : language === 'ru' ? 'объявлений' : 'listings'}
                </span>
              </div>
              {/* Trust Score badge — surfaces seller reliability based on
                  refund rate + dispute outcomes. Hidden when level === 'low'
                  to avoid embarrassing sellers; admin still sees the data. */}
              {trustScore && trustScore.trust_level && trustScore.trust_level !== 'low' && (
                <div className="flex items-center gap-2 mt-3" data-testid="trust-score-badge">
                  {(() => {
                    const lv = trustScore.trust_level;
                    const config = {
                      excellent: { bg: 'bg-gradient-to-r from-emerald-500 to-green-600', lv: 'Izcili uzticams', en: 'Excellent', icon: ShieldCheck },
                      good:      { bg: 'bg-gradient-to-r from-green-500 to-emerald-500', lv: 'Uzticams pārdevējs', en: 'Trusted seller', icon: ShieldCheck },
                      fair:      { bg: 'bg-gradient-to-r from-amber-500 to-orange-500',  lv: 'Aktīvs pārdevējs', en: 'Active seller', icon: ShieldCheck },
                      new:       { bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',   lv: 'Jauns pārdevējs', en: 'New seller', icon: Award },
                    }[lv] || { bg: 'bg-gray-500', lv: 'Pārdevējs', en: 'Seller', icon: ShieldCheck };
                    const TrustIcon = config.icon;
                    return (
                      <div
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-xs font-semibold shadow-md ${config.bg}`}
                        title={
                          lv === 'new'
                            ? (language === 'lv' ? 'Vēl nepietiekami pasūtījumi, lai aprēķinātu reitingu' : language === 'ru' ? 'Пока недостаточно заказов для расчёта рейтинга' : 'Not enough sales yet to compute a rating')
                            : (language === 'lv'
                                ? `${trustScore.customer_satisfaction_pct}% klientu apmierinātība, ${trustScore.total_orders} pasūtījumi`
                                : language === 'ru'
                                ? `${trustScore.customer_satisfaction_pct}% удовлетворённость клиентов, ${trustScore.total_orders} заказов`
                                : `${trustScore.customer_satisfaction_pct}% customer satisfaction, ${trustScore.total_orders} orders`)
                        }
                      >
                        <TrustIcon className="w-3.5 h-3.5" />
                        <span>{language === 'lv' ? config.lv : config.en}</span>
                        {lv !== 'new' && trustScore.customer_satisfaction_pct !== null && (
                          <span className="bg-white/25 px-1.5 rounded-full text-[10px] font-bold">
                            {trustScore.customer_satisfaction_pct}%
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {/* TOP badges per region — gamification: shows where this shop
                  ranks among local sellers. Linkifies to the region landing
                  page so a buyer can see the full leaderboard. */}
              {topBadges.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="shop-top-badges">
                  {topBadges.map((b) => {
                    const RankIcon = b.rank === 1 ? Crown : b.rank === 2 ? Medal : Award;
                    const accent = b.rank === 1 ? '#D97736' : b.rank === 2 ? '#E5E7EB' : '#FDE68A';
                    return (
                      <Link
                        key={`${b.rank}-${b.region_slug}`}
                        to={`/region/${b.region_slug}`}
                        className="group flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 hover:bg-white text-[#121212] text-xs font-semibold shadow-md transition-all"
                        data-testid={`top-badge-${b.rank}-${b.region_slug}`}
                        title={language === 'lv' ? `TOP ${b.rank} veikals ${b.region_name}` : language === 'ru' ? `TOP ${b.rank} магазин ${b.region_name}` : `TOP ${b.rank} shop in ${b.region_name}`}
                      >
                        <RankIcon className="w-3.5 h-3.5" style={{ color: accent }} />
                        <span>TOP {b.rank}</span>
                        <span className="text-[#5C5C5C] group-hover:text-[#D97736]">·  {b.region_name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ===== ABOUT + CONTACT ROW ===== */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 -mt-6 relative z-10 mb-10">
          {/* About */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E5E4E0] p-6 shadow-sm">
            <h2 className="font-outfit font-semibold text-xl text-[#121212] mb-3">
              {language === 'lv' ? 'Par mums' : language === 'ru' ? 'О нас' : 'About Us'}
            </h2>
            {shopDesc ? (
              <p className="text-[#5C5C5C] leading-relaxed whitespace-pre-wrap">{shopDesc}</p>
            ) : (
              <p className="text-[#5C5C5C] italic">
                {language === 'lv' ? 'Apraksts nav pievienots' : language === 'ru' ? 'Описание не добавлено' : 'No description added'}
              </p>
            )}
          </div>

          {/* Contact Card — slimmed down: the full contacts + requisites now
              live in the standalone footer, so keep only what the footer lacks
              (hours, languages) + the quick-message CTA. */}
          <div className="bg-white rounded-2xl border border-[#E5E4E0] p-6 shadow-sm">
            <h2 className="font-outfit font-semibold text-xl text-[#121212] mb-4">
              {language === 'lv' ? 'Info' : language === 'ru' ? 'Инфо' : 'Info'}
            </h2>
            <div className="space-y-3">
              {shop_profile?.shop_working_hours && (
                <div className="flex items-start gap-3 p-3 bg-[#F3F2EE] rounded-xl" data-testid="shop-working-hours">
                  <Clock className="w-5 h-5 text-[#4A6B53] mt-0.5" />
                  <div>
                    <p className="text-[10px] font-semibold text-[#5C5C5C] uppercase tracking-wide mb-0.5">
                      {language === 'lv' ? 'Darba laiks' : language === 'ru' ? 'Часы работы' : 'Working hours'}
                    </p>
                    <span className="text-[#121212] text-sm whitespace-pre-line">{shop_profile.shop_working_hours}</span>
                  </div>
                </div>
              )}

              {Array.isArray(shop_profile?.shop_languages) && shop_profile.shop_languages.length > 0 && (
                <div className="flex items-start gap-3 p-3 bg-[#F3F2EE] rounded-xl" data-testid="shop-languages">
                  <Globe className="w-5 h-5 text-[#4A6B53] mt-0.5" />
                  <div>
                    <p className="text-[10px] font-semibold text-[#5C5C5C] uppercase tracking-wide mb-1">
                      {language === 'lv' ? 'Apkalpošanas valodas' : language === 'ru' ? 'Языки обслуживания' : 'Service languages'}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {shop_profile.shop_languages.map((code) => {
                        const map = {
                          lv: { lv: 'Latviešu', en: 'Latvian' },
                          en: { lv: 'Angļu', en: 'English' },
                          ru: { lv: 'Krievu', en: 'Russian' },
                          de: { lv: 'Vācu', en: 'German' },
                          et: { lv: 'Igauņu', en: 'Estonian' },
                          lt: { lv: 'Lietuviešu', en: 'Lithuanian' },
                        };
                        const label = map[code] ? (language === 'lv' ? map[code].lv : map[code].en) : code;
                        return (
                          <span key={code} className="text-xs px-2 py-0.5 rounded-full bg-white border border-[#E5E4E0] text-[#121212]">
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Requisites removed — now shown in the standalone footer. */}

              {/* Quick Message */}
              {currentUser && !isOwner && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-[#D97736] hover:bg-[#C06528] text-white mt-2" data-testid="contact-btn">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {language === 'lv' ? 'Sazināties' : language === 'ru' ? 'Связаться' : 'Contact'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{language === 'lv' ? 'Sūtīt ziņu' : language === 'ru' ? 'Отправить сообщение' : 'Send Message'}</DialogTitle>
                    </DialogHeader>
                    {messageSent ? (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-center">
                        {language === 'lv' ? 'Ziņa nosūtīta!' : language === 'ru' ? 'Сообщение отправлено!' : 'Message sent!'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Textarea
                          value={messageText} onChange={(e) => setMessageText(e.target.value)}
                          placeholder={language === 'lv' ? 'Jūsu ziņa...' : language === 'ru' ? 'Ваше сообщение...' : 'Your message...'}
                          rows={4} className="resize-none" data-testid="message-textarea"
                        />
                        <Button onClick={handleSendMessage} disabled={!messageText.trim()} className="w-full bg-[#D97736] hover:bg-[#C06528] text-white">
                          <Send className="w-4 h-4 mr-2" /> {language === 'lv' ? 'Nosūtīt' : language === 'ru' ? 'Отправить' : 'Send'}
                        </Button>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </section>

        {/* ===== CERTIFICATES ===== */}
        <section className="mb-10">
          <ShopDocumentsPublic userId={userId} />
        </section>

        {/* ===== PRODUCTS / LISTINGS ===== */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-outfit text-2xl font-bold text-[#121212]">
              {language === 'lv' ? 'Produkti un pakalpojumi' : language === 'ru' ? 'Товары и услуги' : 'Products & Services'}
            </h2>
            {isOwner && (
              <Link to="/create-listing">
                <Button size="sm" className="bg-[#D97736] hover:bg-[#C06528] text-white">
                  + {t('createListing')}
                </Button>
              </Link>
            )}
          </div>

          {/* Shop Collections — hero cards. Only visible when the shop has any. */}
          {collections.length > 0 && (
            <div className="mb-8" data-testid="shop-collections-hero">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="font-outfit text-xl font-semibold text-[#121212]">
                  {language === 'lv' ? 'Kolekcijas' : language === 'ru' ? 'Коллекции' : 'Collections'}
                </h3>
                {activeCollection && (
                  <button
                    onClick={handleClearCollection}
                    className="text-sm text-[#D97736] hover:underline"
                    data-testid="clear-collection-filter"
                  >
                    {language === 'lv' ? 'Rādīt visu ✕' : language === 'ru' ? 'Показать все ✕' : 'Show all ✕'}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {collections.map(coll => {
                  const isActive = activeCollection === coll.id;
                  const count = listings.filter(l => (l.collection_ids || []).includes(coll.id)).length;
                  return (
                    <button
                      key={coll.id}
                      onClick={() => handleCollectionToggle(coll)}
                      className={`group relative overflow-hidden rounded-2xl border transition-all text-left ${
                        isActive
                          ? 'border-[#D97736] ring-2 ring-[#D97736]/30 shadow-md'
                          : 'border-[#E5E4E0] hover:-translate-y-0.5 hover:shadow-md'
                      }`}
                      data-testid={`collection-card-${coll.id}`}
                    >
                      <div className="aspect-[5/3] bg-gradient-to-br from-[#FFF8F0] to-[#FAFAFA] relative overflow-hidden">
                        {coll.image_url ? (
                          <img src={resolveAssetUrl(coll.image_url)} alt={coll.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#D97736]/30 text-3xl font-outfit font-bold">
                            {coll.name?.charAt(0).toUpperCase() || '·'}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                          <div className="font-outfit font-semibold leading-tight line-clamp-1">{coll.name}</div>
                          <div className="text-xs text-white/80 mt-0.5">
                            {count} {language === 'lv' ? 'sludinājumi' : language === 'ru' ? 'объявлений' : 'listings'}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Category filter tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {categoryTabs.map((tab) => {
              const count = tab === 'all' ? listings.length : listings.filter(l => l.category === tab).length;
              if (tab !== 'all' && count === 0) return null;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    activeTab === tab
                      ? 'text-white shadow-md'
                      : 'bg-white border border-[#E5E4E0] text-[#5C5C5C]'
                  }`}
                  style={activeTab === tab ? { backgroundColor: secondary } : undefined}
                  data-testid={`tab-${tab}`}
                >
                  {categoryLabels[tab]} {count > 0 && `(${count})`}
                </button>
              );
            })}
          </div>

          {filteredListings.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredListings.map((listing) => (
                <div
                  key={listing.id}
                  onClick={(e) => { e.preventDefault(); setSelectedListing(listing); }}
                  className="cursor-pointer"
                  data-testid={`shop-listing-${listing.id}`}
                >
                  <div className="pointer-events-none">
                    <ListingCard listing={listing} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-[#E5E4E0]">
              <Store className="w-16 h-16 mx-auto mb-4 text-[#5C5C5C] opacity-20" />
              <h3 className="font-outfit text-xl font-semibold text-[#121212] mb-2">
                {language === 'lv' ? 'Nav produktu' : language === 'ru' ? 'Пока нет товаров' : 'No products yet'}
              </h3>
              {isOwner && (
                <Link to="/create-listing">
                  <Button className="mt-4 bg-[#D97736] hover:bg-[#C06528] text-white">
                    {t('createListing')}
                  </Button>
                </Link>
              )}
            </div>
          )}
        </section>

        {/* ===== LISTING MODAL ===== */}
        {selectedListing && (
          <Dialog open={!!selectedListing} onOpenChange={(open) => { if (!open) setSelectedListing(null); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-outfit text-xl">{selectedListing.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {selectedListing.images?.length > 0 ? (
                  <div className="aspect-[16/10] rounded-xl overflow-hidden bg-[#F3F2EE]">
                    <img
                      src={resolveAssetUrl(selectedListing.images[0])}
                      alt={selectedListing.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] rounded-xl bg-[#F3F2EE] flex items-center justify-center">
                    <Store className="w-16 h-16 text-[#D97736]/30" />
                  </div>
                )}
                {selectedListing.price > 0 && (
                  <div className="text-2xl font-bold text-[#D97736] font-outfit">
                    €{selectedListing.price}
                    {selectedListing.price_type === 'hourly' && '/h'}
                    {selectedListing.price_type === 'negotiable' && (
                      <span className="text-sm font-normal text-[#5C5C5C] ml-2">
                        ({language === 'lv' ? 'pēc vienošanās' : language === 'ru' ? 'по договорённости' : 'negotiable'})
                      </span>
                    )}
                  </div>
                )}
                {selectedListing.price_type === 'negotiable' && (!selectedListing.price || selectedListing.price <= 0) && (
                  <Badge className="bg-[#D97736] text-white border-0">{language === 'lv' ? 'Pēc vienošanās' : language === 'ru' ? 'По договорённости' : 'Negotiable'}</Badge>
                )}
                {selectedListing.price_type === 'free' && (
                  <Badge className="bg-green-500 text-white border-0">{language === 'lv' ? 'Bezmaksas' : language === 'ru' ? 'Бесплатно' : 'Free'}</Badge>
                )}
                <p className="text-[#5C5C5C] whitespace-pre-wrap">{selectedListing.description}</p>
                {(selectedListing.contact_email || selectedListing.address) && (
                  <div className="space-y-2 p-4 bg-[#F3F2EE] rounded-xl">
                    {selectedListing.contact_email && (
                      <a href={`mailto:${selectedListing.contact_email}`} className="flex items-center gap-2 text-sm text-[#121212] hover:text-[#D97736]">
                        <Mail className="w-4 h-4 text-[#4A6B53]" /> {selectedListing.contact_email}
                      </a>
                    )}
                    {selectedListing.address && (
                      <div className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                        <MapPin className="w-4 h-4 text-[#4A6B53]" /> {selectedListing.address}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  {currentUser && currentUser._id !== shopUser._id && selectedListing.price > 0 && (
                    <Button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/cart/add`, { listing_id: selectedListing.id, quantity: 1 });
                          alert(language === 'lv' ? 'Pievienots grozam!' : language === 'ru' ? 'Добавлено в корзину!' : 'Added to cart!');
                        } catch (e) { console.error(e); }
                      }}
                      className="flex-1 bg-[#4A6B53] hover:bg-[#3a5a43] text-white"
                      data-testid="modal-add-to-cart-btn"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {language === 'lv' ? 'Pievienot grozam' : language === 'ru' ? 'Добавить в корзину' : 'Add to Cart'}
                    </Button>
                  )}
                  <Link to={`/listing/${selectedListing.id}`} className="flex-1">
                    <Button variant="outline" className="w-full border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white" data-testid="modal-view-full-btn">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {language === 'lv' ? 'Pilns skats' : language === 'ru' ? 'Полный вид' : 'Full View'}
                    </Button>
                  </Link>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* ===== REVIEWS ===== */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl border border-[#E5E4E0] p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-2xl font-bold text-[#121212]">
                {language === 'lv' ? 'Atsauksmes' : language === 'ru' ? 'Отзывы' : 'Reviews'}
              </h2>
              {reviewStats.total > 0 && (
                <div className="flex items-center gap-3">
                  <div className="text-3xl font-bold text-[#D97736] font-outfit">{reviewStats.avg_rating}</div>
                  <div>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`w-4 h-4 ${s <= Math.round(reviewStats.avg_rating) ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
                      ))}
                    </div>
                    <p className="text-xs text-[#5C5C5C]">{reviewStats.total} {language === 'lv' ? 'atsauksmes' : language === 'ru' ? 'отзывов' : 'reviews'}</p>
                  </div>
                </div>
              )}
            </div>

            {reviews.length > 0 ? (
              <div className="space-y-5">
                {reviews.slice(0, 10).map(review => (
                  <div key={review.id} className="flex gap-4 pb-5 border-b border-[#E5E4E0] last:border-b-0 last:pb-0">
                    <div className="w-10 h-10 rounded-full bg-[#4A6B53] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                      {review.user_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-[#121212] text-sm">{review.user_name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-3.5 h-3.5 ${s <= review.rating ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`} />
                          ))}
                        </div>
                        <span className="text-xs text-[#5C5C5C]">
                          {new Date(review.created_at).toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US')}
                        </span>
                      </div>
                      <p className="text-sm text-[#5C5C5C]">{review.comment}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#5C5C5C] py-8">
                {language === 'lv' ? 'Vēl nav atsauksmju' : language === 'ru' ? 'Пока нет отзывов' : 'No reviews yet'}
              </p>
            )}
          </div>
        </section>
      </div>
      {c.banner_bottom_html && (
        <div data-testid="shop-banner-bottom" dangerouslySetInnerHTML={{ __html: c.banner_bottom_html }} />
      )}
      <CompanyFooter
        theme="light"
        name={shopName}
        logo={shop_profile?.shop_logo_url}
        contacts={{
          phone: shop_profile?.shop_phone || shopUser.phone,
          email: shopUser.email,
          address: shop_profile?.legal_address,
          facebook: shop_profile?.shop_facebook,
          instagram: shop_profile?.shop_instagram,
          tiktok: shop_profile?.shop_tiktok,
          youtube: shop_profile?.shop_youtube,
          whatsapp: shop_profile?.shop_whatsapp,
        }}
        requisites={{
          legal_name: shop_profile?.legal_name,
          registration_number: shop_profile?.registration_number,
          vat_number: shop_profile?.vat_number,
          legal_address: shop_profile?.legal_address,
        }}
      />
    </div>
  );
}
